import { requestJson } from './apiClient';
import { AI_ASSISTANT_URL, TENANT_ID, isAiApiConfigured } from './endpoints';

/**
 * AI assistant service.
 *
 * Defines the contract the assistant chat uses. It performs no inference,
 * holds no prompts, and ships no model credentials: calls travel
 * React -> HTTPS -> Lambda Function URL -> Lambda, with the Lambda reading the
 * DeepSeek key from AWS Secrets Manager. The URL and the tenant ID come from
 * environment variables at build time.
 *
 * ============================================================================
 * POLICY BOUNDARY -- the assistant, when implemented, MUST NOT:
 *   - provide personalised tax advice
 *   - provide legal advice
 *   - represent itself as a CPA or as a CPA firm
 *   - claim to perform audits, attest, or review engagements
 *   - request SSNs, EINs, bank credentials, card numbers, or tax returns
 *   - make guaranteed financial or tax claims
 * It MAY explain services, answer general questions about the engagement
 * process, collect basic lead information, and hand conversations to a human.
 * ============================================================================
 */

/**
 * Longest message the assistant endpoint accepts.
 *
 * The Lambda truncates anything longer silently, so the UI stops at the same
 * number: a visitor sees the limit as a count rather than discovering it as a
 * shortened question.
 */
export const ASSISTANT_MESSAGE_MAX_LENGTH = 1000;

/** Draft length at which the counter appears. Below it, the box stays clean. */
export const ASSISTANT_COUNTER_VISIBLE_FROM = 800;

/** Fixed opening message, so the assistant never introduces itself as a CPA. */
export const ASSISTANT_GREETING =
  'Hello. I can answer general questions about our bookkeeping services for California small businesses and help you schedule a consultation. I am not a CPA and cannot provide tax or legal advice.';

/**
 * sessionStorage key holding the id of the conversation in progress.
 *
 * The Lambda keeps its booking state machine against `conversationId`, so the
 * id has to outlive a refresh for a visitor to reload mid-booking and pick the
 * conversation up where they left it. sessionStorage, not component state: it
 * belongs to this tab, a refresh keeps it, and closing the tab is how a visitor
 * asks for a fresh conversation.
 */
export const CONVERSATION_ID_STORAGE_KEY = 'chatConversationId';

/**
 * In-memory copy of the conversation id.
 *
 * Storage is unreadable in some privacy modes and inside a sandboxed iframe,
 * where access throws rather than returning null. The copy keeps a conversation
 * together for the life of the page in exactly those cases.
 */
let conversationIdInMemory = null;

/**
 * Id of the conversation in progress, or null when the Lambda has not minted one
 * yet. A null omits the field from the next request, which is what starts a
 * conversation: the Lambda mints an id and returns it on that reply.
 */
export const getConversationId = () => {
  if (conversationIdInMemory) return conversationIdInMemory;

  try {
    const stored = window.sessionStorage.getItem(CONVERSATION_ID_STORAGE_KEY);
    if (stored) conversationIdInMemory = stored;
  } catch {
    // Unreadable storage: fall through and let the Lambda mint a fresh id.
  }

  return conversationIdInMemory;
};

/**
 * Records the id the Lambda returned, so the next turn joins the same
 * conversation. Best-effort: an id that cannot be persisted still carries this
 * page's turns through the in-memory copy above.
 */
export const setConversationId = (id) => {
  if (!id || typeof id !== 'string') return;

  conversationIdInMemory = id;

  try {
    window.sessionStorage.setItem(CONVERSATION_ID_STORAGE_KEY, id);
  } catch {
    // Storage unavailable; the copy above is what the chat uses.
  }
};

/**
 * Forgets the conversation, so the next turn starts a new one.
 *
 * Nothing calls this yet: the widget deliberately keeps an in-progress
 * conversation when a visitor moves between marketing pages. It is the call a
 * future "New chat" affordance makes, and by hand it is
 * `sessionStorage.removeItem('chatConversationId')` during UAT.
 */
export const clearConversationId = () => {
  conversationIdInMemory = null;

  try {
    window.sessionStorage.removeItem(CONVERSATION_ID_STORAGE_KEY);
  } catch {
    // Nothing to remove.
  }
};

/**
 * Most recent messages sent as context on every request.
 *
 * Matches the cap the Lambda applies to its own `messages` array, so the window
 * the browser sends is the window the Lambda would keep anyway: a long
 * conversation costs no more than a short one.
 */
export const ASSISTANT_HISTORY_LIMIT = 20;

/** Role the transcript uses for the visitor. The wire name is `user`. */
const ROLE_VISITOR = 'visitor';

/**
 * Builds the `messages` array for one request: the conversation so far, in
 * order, followed by the question being asked now.
 *
 * The Lambda keeps its conversation state -- the booking flow's step -- against
 * `conversationId`, but the model only sees what travels here. A turn sent on
 * its own leaves a short reply such as "Yes" answering "would you like me to
 * check times?" with nothing to attach to, which reads as an isolated off-topic
 * word and fires the boundary refusal.
 *
 * The transcript is the widget's view of the conversation, so it does not map
 * one-to-one onto the wire:
 *   - the array starts at the visitor's first turn. The transcript opens with
 *     the greeting, which is UI decoration -- neither side said it -- and
 *     sending it would read as something the assistant had already claimed;
 *   - error notices are left out. They are transport failures rendered into the
 *     transcript, not answers, and sending one would invent an assistant turn
 *     that never happened;
 *   - an entry with no text left after trimming is left out too: a turn with
 *     nothing in it is not a turn;
 *   - the question is appended here rather than read off the transcript's last
 *     entry: React state updates are asynchronous, so the transcript the caller
 *     holds while it sends does not yet contain the turn in flight.
 *
 * @param {string} message - the question being asked now
 * @param {Array<object>} transcript - the rendered transcript, greeting included
 * @returns {Array<{role: 'user'|'assistant', content: string}>}
 */
export const buildAssistantMessages = (message, transcript = []) => {
  const turns = transcript
    .filter((entry) => entry?.text?.trim() && !entry.isError)
    .map((entry) => ({
      role: entry.role === ROLE_VISITOR ? 'user' : 'assistant',
      content: entry.text.trim(),
    }));

  // Everything before the visitor's first turn is decoration, not conversation.
  const firstTurn = turns.findIndex((turn) => turn.role === 'user');

  return [
    ...(firstTurn === -1 ? [] : turns.slice(firstTurn)),
    { role: 'user', content: message },
  ].slice(-ASSISTANT_HISTORY_LIMIT);
};

/**
 * Sends a message to the assistant endpoint.
 *
 * When the Lambda Function URL is configured the turn is posted with the tenant
 * ID, the conversation id when one is stored, and the whole conversation in
 * order -- capped at ASSISTANT_HISTORY_LIMIT, greeting excluded -- because the
 * Lambda keeps its booking state against the id but the model sees only what
 * travels here. The id the Lambda returns is stored before the reply is handed
 * back, so the next turn continues the same conversation. When the endpoint is
 * not configured the call reports `not-configured` rather than inventing an
 * answer.
 *
 * No model provider is contacted from the browser and no credential is sent:
 * only the shared Lambda talks to DeepSeek.
 *
 * @param {string} message - visitor message
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {Array<object>} [options.history] - transcript so far, greeting included
 * @returns {Promise<{status: string, message: string}>}
 */
export const sendAssistantMessage = async (
  message,
  { signal, history = [] } = {}
) => {
  // The shared Lambda is not path-routed, so the Function URL is used as-is.
  const url = AI_ASSISTANT_URL;

  if (!url || !isAiApiConfigured()) {
    return {
      status: 'not-configured',
      message:
        'The assistant is not enabled in this build. Please use the consultation form or contact us directly and a person will help you.',
    };
  }

  try {
    // Null on the first turn of a visit. The field is then omitted entirely --
    // the Lambda mints an id, returns it, and it is stored below.
    const conversationId = getConversationId();

    const response = await requestJson(url, {
      method: 'POST',
      body: {
        // The assistant Lambda is shared between sites, so every request must
        // say which tenant it belongs to. Tenant values are validated on the
        // server; this field is not a security boundary.
        tenantId: TENANT_ID,
        // Ties this turn to the conversation the Lambda is keeping state for.
        // Without it every turn is a new conversation, which is what stops a
        // multi-turn booking flow from advancing past its first step.
        ...(conversationId ? { conversationId } : {}),
        // Payload shape matches the deployed Lambda contract (the same one
        // qaaslab-ai-site calls): a messages array rather than a bare string.
        // The whole conversation travels, in order, ending with this question.
        // The Lambda keeps the booking flow's step against the id above, but the
        // model sees only what travels here, so a turn sent alone leaves a short
        // reply like "Yes" with no context to attach to.
        messages: buildAssistantMessages(message, history),
      },
      signal,
    });

    // The Lambda returns the id on every response, the first one included --
    // that first reply is where a new conversation's id comes from. Storing it
    // here rather than in the hook keeps the whole wire contract in one module.
    setConversationId(response?.conversationId);

    return {
      status: 'ok',
      message: response?.reply || response?.message || '',
    };
  } catch (error) {
    return {
      status: 'error',
      message:
        error?.message ||
        'The assistant is unavailable right now. Please use the consultation form and we will follow up.',
    };
  }
};

export default sendAssistantMessage;
